import { useState } from "react";
import type { FormEvent } from "react";

interface BootstrapFormProps {
  onSubmit: (password: string) => Promise<void>;
  busy: boolean;
  error: string | null;
}

/**
 * The break-glass "claim this deployment" form. There is no unauthenticated way to know in
 * advance whether bootstrap is actually available — the backend deliberately reports "wrong
 * password" and "already claimed" with the same vague 403 so this form can't be used to probe
 * that (see `AdminAuthError` in `backend/security.py`). So this form is always offered to a
 * signed-in non-admin; a deployment that already has admins will simply refuse it, correctly and
 * without a more specific error message.
 */
export function BootstrapForm({ onSubmit, busy, error }: BootstrapFormProps): JSX.Element {
  const [password, setPassword] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (password) {
      void onSubmit(password);
    }
  };

  return (
    <form className="bootstrap-form" onSubmit={handleSubmit}>
      <label htmlFor="bootstrap-password">Bootstrap password</label>
      <input
        id="bootstrap-password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        disabled={busy}
        required
      />
      {error && <p className="field-error">{error}</p>}
      <button type="submit" className="button button--primary" disabled={busy || !password}>
        {busy ? "Claiming…" : "Become the first administrator"}
      </button>
    </form>
  );
}
